/**
 * @name GitInfoService.ts
 * @version 1.0.0
 * @description Servicio para obtener información de git desde las variables capturadas durante el build
 * @author
 * @changelog
 * 1.0.0 - 2024/12/19 - @santiago - Servicio inicial
 */

import { GIT_CONFIG } from '../config/git-config'

/**
 * Interfaz para el resumen de información de git
 */
export interface IGitSummary {
	hash: string
	shortHash: string
	branch: string
	version: string
	lastCommitDate: string
	lastDeployDate?: string
	changedFiles: string
	lastTag?: string
}

/**
 * Servicio para obtener información de git
 */
export class GitInfoService {
	/**
	 * Obtiene el resumen completo de información de git
	 */
	public static getGitSummary(): IGitSummary {
		return {
			hash: GIT_CONFIG.GIT_HASH,
			shortHash: GIT_CONFIG.GIT_SHORT_HASH,
			branch: GIT_CONFIG.GIT_BRANCH,
			version: GIT_CONFIG.GIT_VERSION,
			lastCommitDate: GIT_CONFIG.GIT_LAST_COMMIT_DATE,
			changedFiles: GIT_CONFIG.GIT_CHANGED_FILES,
			lastDeployDate: GIT_CONFIG.LAST_DEPLOY_DATE,
			lastTag: GIT_CONFIG.LAST_TAG
		}
	}

	/**
	 * Obtiene el hash completo de git
	 */
	public static getGitHash(): string {
		return GIT_CONFIG.GIT_HASH
	}

	/**
	 * Obtiene el hash corto de git
	 */
	public static getShortGitHash(): string {
		return GIT_CONFIG.GIT_SHORT_HASH
	}

	/**
	 * Obtiene la rama actual
	 */
	public static getCurrentBranch(): string {
		return GIT_CONFIG.GIT_BRANCH
	}

	/**
	 * Obtiene la versión de git
	 */
	public static getGitVersion(): string {
		return GIT_CONFIG.GIT_VERSION
	}

	/**
	 * Obtiene la fecha del último commit
	 */
	public static getLastCommitDate(): string {
		return GIT_CONFIG.GIT_LAST_COMMIT_DATE
	}

	/**
	 * Obtiene la fecha del último deploy
	 */
	public static getLastDeployDate(): string {
		return GIT_CONFIG.LAST_DEPLOY_DATE
	}

	/**
	 * Obtiene los archivos modificados
	 */
	public static getChangedFiles(): string {
		return GIT_CONFIG.GIT_CHANGED_FILES
	}

	/**
	 * Obtiene el último tag
	 */
	public static getLastTag(): string {
		return GIT_CONFIG.LAST_TAG
	}

	/**
	 * Obtiene información formateada para mostrar en la interfaz
	 */
	public static getFormattedInfo() {
		const summary = this.getGitSummary()
		return {
			version: `${summary.lastTag === 'unknown' ? 'desconocida' : summary.lastTag}`,
			commit: `${summary.shortHash} (${summary.branch})`,
			lastCommit: new Date(summary.lastCommitDate)
				.toLocaleString('es-ES', {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
					hour: '2-digit',
					minute: '2-digit',
					second: '2-digit',
					hour12: false
				})
				.replace(/[\/,-]/g, '/'),
			lastDeploy: new Date(summary.lastDeployDate ?? '')
				.toLocaleString('es-ES', {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
					hour: '2-digit',
					minute: '2-digit',
					second: '2-digit',
					hour12: false
				})
				.replace(/[\/,-]/g, '/'),
			hasChanges: summary.changedFiles.length > 0
		}
	}
}
